import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './PostAd.css';
import { apiUrl } from '../api';

const categories = [
  // Tech & Gadgets
  { id: 1, label: 'Mobile Phones' },
  { id: 2, label: 'Electronics' },

  // Automotive
  { id: 3, label: 'Cars' },
  { id: 4, label: 'Bikes' },
  { id: 5, label: 'Automotive Accessories' },

  // Real Estate & Home
  { id: 6, label: 'Furniture' },
  { id: 7, label: 'Rent: Houses & flats' },
  { id: 8, label: 'Buy: Houses & flats' },
  { id: 9, label: 'Tools & Home Improvement' },

  // Hobbies, Recreation & Leisure
  { id: 10, label: 'Books' },
  { id: 11, label: 'Jewelry & Watches' },
  { id: 12, label: 'Clothing' },
  { id: 13, label: 'Garden & Outdoors' },
  { id: 14, label: 'Sports' },
  { id: 15, label: 'Musical Instruments' },
  { id: 16, label: 'Art & Craft Supplies' },
  { id: 17, label: 'Office & Stationery' },

  // Home & Kitchen
  { id: 18, label: 'Home & Kitchen' }
];

const conditions = [
  { value: 'new', label: 'New' },
  { value: 'like-new', label: 'Like New' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' }
];

function PostAd({ token }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    categoryId: '1',
    price: '',
    condition: 'good',
    city: '',
    locality: ''
  });
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [adCredits, setAdCredits] = useState(null);

  useEffect(() => {
    if (!token) {
      navigate('/login');
    }
    // Fetch user's ad credits
    const fetchProfile = async () => {
      try {
        const res = await fetch(apiUrl('/api/auth/profile'), { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        setAdCredits(data.user?.ad_credits ?? 0);
      } catch (e) {}
    };
    if (token) fetchProfile();
  }, [navigate, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = name === 'price' ? value.replace(/[^0-9]/g, '') : value;
    setFormData((prev) => ({ ...prev, [name]: sanitizedValue }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 7); // limit to 7
    setImages(files);
    const previews = files.map((file) => URL.createObjectURL(file));
    setImagePreviews(previews);
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (adCredits !== null && adCredits <= 0) {
      setError('You have no ad credits remaining. Please wait for renewal.');
      return;
    }

    if (!formData.title.trim() || !formData.description.trim() || !formData.city.trim() || !formData.locality.trim()) {
      setError('Please complete all required fields before posting your ad.');
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('title', formData.title);
    formDataToSend.append('description', formData.description);
    formDataToSend.append('categoryId', formData.categoryId);
    formDataToSend.append('price', formData.price);
    formDataToSend.append('condition', formData.condition);
    formDataToSend.append('city', formData.city);
    formDataToSend.append('locality', formData.locality);

    // Compress images in browser before upload
    const compressImage = (file, maxWidth = 1024, quality = 0.7) => new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = async () => {
        try {
          const ratio = Math.min(1, maxWidth / img.width);
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width * ratio);
          canvas.height = Math.round(img.height * ratio);
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
          }, 'image/jpeg', quality);
        } catch (err) { reject(err); }
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });

    // Only allow up to 7 images
    const filesToUpload = images.slice(0, 7);
    for (const file of filesToUpload) {
      try {
        const compressed = await compressImage(file, 1024, 0.75);
        formDataToSend.append('images', compressed);
      } catch (err) {
        // fallback to original file
        formDataToSend.append('images', file);
      }
    }

    setLoading(true);

    try {
      await axios.post(apiUrl('/api/products'), formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      // refresh credits
      try { const r = await fetch(apiUrl('/api/auth/profile'), { headers: { Authorization: `Bearer ${token}` } }); if (r.ok) { const d = await r.json(); setAdCredits(d.user?.ad_credits ?? adCredits); } } catch(e){}
      setSuccess('Your ad has been posted successfully. Redirecting...');
      setTimeout(() => {
        navigate('/my-listings');
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not post ad. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="post-ad-page">
      <div className="post-ad-card">
        <h2>Post a New Ad</h2>
        <p>Create a listing for what you want to sell.</p>
        <div className="credits-row">
          <strong>Ad Credits:</strong> {adCredits === null ? 'Loading...' : `${adCredits} remaining`}
        </div>

        {error && <div className="form-error">{error}</div>}
        {success && <div className="form-success">{success}</div>}

        <form className="post-ad-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="title">Title</label>
            <input
              id="title"
              name="title"
              type="text"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g. Samsung Galaxy S21"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe the item, condition, and features"
              rows={5}
              required
              disabled={loading}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="categoryId">Category</label>
              <select
                id="categoryId"
                name="categoryId"
                value={formData.categoryId}
                onChange={handleChange}
                disabled={loading}
              >
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="condition">Condition</label>
              <select
                id="condition"
                name="condition"
                value={formData.condition}
                onChange={handleChange}
                disabled={loading}
              >
                {conditions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="price">Price</label>
              <input
                id="price"
                name="price"
                type="number"
                min="0"
                inputMode="numeric"
                value={formData.price}
                onChange={handleChange}
                onWheel={(e) => e.target.blur()}
                placeholder="Enter price"
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="images">Upload Images (up to 7)</label>
            <input
              id="images"
              name="images"
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageChange}
              disabled={loading}
            />
          </div>

          {imagePreviews.length > 0 && (
            <div className="image-previews">
              <p>Preview ({imagePreviews.length}):</p>
              <div className="preview-grid">
                {imagePreviews.map((preview, index) => (
                  <div key={index} className="preview-item">
                    <img src={preview} alt={`Preview ${index + 1}`} />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="remove-btn"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="city">City</label>
              <input
                id="city"
                name="city"
                type="text"
                value={formData.city}
                onChange={handleChange}
                placeholder="City"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="locality">Locality</label>
              <input
                id="locality"
                name="locality"
                type="text"
                value={formData.locality}
                onChange={handleChange}
                placeholder="Locality / Neighborhood"
                required
                disabled={loading}
              />
            </div>
          </div>

          <button type="submit" className="submit-btn" disabled={loading || (adCredits !== null && adCredits <= 0)}>
            {loading ? 'Posting your ad...' : 'Post Ad'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default PostAd;
